// OTA folder structure status — exact state of the ota-artifacts R2 bucket.
// Data: GET /api/admin/ota/status (useOtaStatus). Layout follows OtaOverviewPage.

import { useMemo, useState, type ReactNode } from 'react';
import {
  ArchiveBoxIcon,
  CheckCircleIcon,
  ChevronRightIcon,
  CubeIcon,
  DocumentTextIcon,
  ExclamationTriangleIcon,
  FolderIcon,
  MapPinIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';
import { AppBadge, Badge, Btn, Card, cx } from '../../../components/admin/console';
import { useOtaStatus } from './data/hooks';
import {
  appOf,
  type OtaAppId,
  type OtaFolderStatus,
  type OtaMissingVariant,
  type OtaPointerStatus,
  type OtaStructureStatus,
} from './data/types';

function fmtBytes(n: number): string {
  if (!n) return '—';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function ago(iso: string | null): string {
  if (!iso) return '—';
  return formatDistanceToNow(new Date(iso), { addSuffix: true });
}

export function OtaStatusPage() {
  const { data: status, isLoading, isError, refetch } = useOtaStatus();
  const [issuesOnly, setIssuesOnly] = useState(false);

  if (isLoading) {
    return (
      <div className="px-8 py-6">
        <Header scannedAt={null} />
        <div className="mt-16 text-center text-sm text-slate-500">Scanning bucket…</div>
      </div>
    );
  }

  if (isError || !status) {
    return (
      <div className="px-8 py-6 space-y-6">
        <Header scannedAt={null} />
        <Card className="border-red-500/40">
          <div className="flex items-center gap-3">
            <XCircleIcon className="w-5 h-5 text-red-400" />
            <div className="flex-1 text-sm text-slate-300">
              The bucket scan failed. The API may be missing its OTA_ARTIFACTS binding.
            </div>
            <Btn variant="outline" size="sm" onClick={() => void refetch()}>
              Retry scan
            </Btn>
          </div>
        </Card>
      </div>
    );
  }

  const { summary, pointers, folders } = status;
  const healthyPointers = pointers.filter((p) => p.isValid).length;

  return (
    <div className="px-8 py-6 space-y-6">
      <Header scannedAt={status.scannedAt} />

      <div className="grid grid-cols-5 gap-4">
        <Kpi
          icon={<FolderIcon className="w-[18px] h-[18px]" />}
          color="cyan"
          title="Build folders"
          value={summary.folders}
          note={`${new Set(folders.map((f) => f.appDir)).size} apps`}
        />
        <Kpi
          icon={<CheckCircleIcon className="w-[18px] h-[18px]" />}
          color="emerald"
          title="Healthy"
          value={summary.healthy}
          note="both files, valid manifest"
        />
        <Kpi
          icon={<ExclamationTriangleIcon className="w-[18px] h-[18px]" />}
          color={summary.degraded ? 'amber' : 'slate'}
          title="Degraded"
          value={summary.degraded}
          note="manifest without sha256"
        />
        <Kpi
          icon={<XCircleIcon className="w-[18px] h-[18px]" />}
          color={summary.broken ? 'red' : 'slate'}
          title="Broken"
          value={summary.broken}
          note="missing file or invalid manifest"
        />
        <Kpi
          icon={<MapPinIcon className="w-[18px] h-[18px]" />}
          color={healthyPointers === pointers.length ? 'emerald' : 'amber'}
          title="Channel pointers"
          value={healthyPointers}
          total={pointers.length}
          note={
            status.legacyPointerKeys.length
              ? `${status.legacyPointerKeys.length} legacy key${status.legacyPointerKeys.length === 1 ? '' : 's'} lingering`
              : 'no legacy keys'
          }
        />
      </div>

      <div className="grid grid-cols-2 gap-6 items-start">
        <PointersCard status={status} />
        <ChecksCard status={status} />
      </div>

      <BucketTreeCard
        status={status}
        issuesOnly={issuesOnly}
        onToggleIssuesOnly={() => setIssuesOnly((v) => !v)}
      />
    </div>
  );
}

function Header({ scannedAt }: { scannedAt: string | null }) {
  return (
    <div className="flex items-start justify-between gap-6">
      <div>
        <div className="text-[11px] font-bold uppercase tracking-[0.2em] text-cyan-400">
          OTA Update Console
        </div>
        <h1 className="text-2xl font-bold text-white mt-1">Folder structure status</h1>
        <p className="text-sm text-slate-400 mt-1">
          Every key in the <span className="font-mono text-slate-300">ota-artifacts</span> bucket,
          checked against the expected layout — channel pointers at the root,{' '}
          <span className="font-mono text-slate-300">app.apk</span> +{' '}
          <span className="font-mono text-slate-300">manifest.json</span> in each build folder.
        </p>
      </div>
      {scannedAt && (
        <div className="text-[11px] text-slate-500 whitespace-nowrap pt-1">
          Scanned {ago(scannedAt)}
        </div>
      )}
    </div>
  );
}

interface KpiProps {
  icon: ReactNode;
  color: 'emerald' | 'amber' | 'red' | 'cyan' | 'slate';
  title: string;
  value: number;
  total?: number;
  note: string;
}

const kpiBg: Record<KpiProps['color'], string> = {
  emerald: 'bg-emerald-500/10 text-emerald-400',
  amber:   'bg-amber-500/10 text-amber-400',
  red:     'bg-red-500/10 text-red-400',
  cyan:    'bg-cyan-500/10 text-cyan-400',
  slate:   'bg-slate-600/20 text-slate-400',
};

function Kpi({ icon, color, title, value, total, note }: KpiProps) {
  return (
    <Card>
      <div className="flex items-center gap-3 mb-3">
        <div className={cx('w-9 h-9 rounded-lg flex items-center justify-center', kpiBg[color])}>
          {icon}
        </div>
        <p className="text-xs text-slate-400 font-semibold uppercase tracking-wider">{title}</p>
      </div>
      <div className="flex items-baseline gap-2">
        <div className="text-3xl font-bold text-white font-mono leading-none">{value}</div>
        {total != null && <div className="text-sm text-slate-500 font-mono">/ {total}</div>}
      </div>
      <div className="mt-2 text-[11px] text-slate-500">{note}</div>
    </Card>
  );
}

function PointerRow({ pointer }: { pointer: OtaPointerStatus }) {
  const tone = pointer.isValid ? 'success' : pointer.exists ? 'error' : 'warning';
  const label = pointer.isValid ? 'Healthy' : pointer.exists ? 'Invalid' : 'Missing';
  return (
    <div className="px-5 py-3 border-b border-slate-700/50 last:border-b-0 flex items-center gap-4">
      <span
        className={cx(
          'w-2 h-2 rounded-full flex-shrink-0',
          pointer.isValid ? 'bg-emerald-400' : pointer.exists ? 'bg-red-400' : 'bg-amber-400',
        )}
      />
      <div className="flex-1 min-w-0">
        <div className="text-[13px] font-mono text-white truncate">{pointer.key}</div>
        <div className="text-[11px] text-slate-500 mt-0.5">
          {pointer.isValid ? (
            <>
              <span className="text-slate-300 font-semibold">{pointer.appCount}</span> app
              {pointer.appCount === 1 ? '' : 's'} promoted · updated {ago(pointer.updatedAt)}
            </>
          ) : (
            pointer.error
          )}
        </div>
      </div>
      <Badge tone={tone}>{label}</Badge>
    </div>
  );
}

function PointersCard({ status }: { status: OtaStructureStatus }) {
  return (
    <Card padded={false} className="overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-700/60">
        <h2 className="text-sm font-bold text-white uppercase tracking-wider">Channel pointers</h2>
        <p className="text-xs text-slate-500 mt-0.5">Bucket root · written only on promote</p>
      </div>
      {status.pointers.map((p) => (
        <PointerRow key={p.key} pointer={p} />
      ))}
      {status.legacyPointerKeys.map((key) => (
        <div
          key={key}
          className="px-5 py-3 border-b border-slate-700/50 last:border-b-0 flex items-center gap-4"
        >
          <span className="w-2 h-2 rounded-full flex-shrink-0 bg-amber-400" />
          <div className="flex-1 min-w-0">
            <div className="text-[13px] font-mono text-white truncate">{key}</div>
            <div className="text-[11px] text-slate-500 mt-0.5">
              Legacy key from the old layout — devices no longer read it. Safe to delete.
            </div>
          </div>
          <Badge tone="warning">Legacy</Badge>
        </div>
      ))}
    </Card>
  );
}

interface CheckRowProps {
  ok: boolean;
  warn?: boolean;
  label: string;
  detail: string;
  count?: string;
}

function CheckRow({ ok, warn = false, label, detail, count }: CheckRowProps) {
  return (
    <div className="px-5 py-3 border-b border-slate-700/50 last:border-b-0 flex items-start gap-3">
      <span
        className={cx(
          'w-2 h-2 rounded-full flex-shrink-0 mt-1.5',
          ok ? 'bg-emerald-400' : warn ? 'bg-amber-400' : 'bg-red-400',
        )}
      />
      <div className="flex-1 min-w-0">
        <div className="text-[13px] text-slate-200">{label}</div>
        <div className="text-[11px] text-slate-500 mt-0.5">{detail}</div>
      </div>
      {ok ? (
        <Badge tone="success">Pass</Badge>
      ) : (
        <span className="text-xs text-slate-400 font-mono whitespace-nowrap">{count}</span>
      )}
    </div>
  );
}

function ChecksCard({ status }: { status: OtaStructureStatus }) {
  const badPointers = status.pointers.filter((p) => !p.isValid);
  const noSha = status.folders.filter((f) => f.health === 'degraded');
  const brokenFolders = status.folders.filter((f) => f.health === 'broken');
  const firstBroken = brokenFolders[0];
  const firstMissing = status.missingVariants[0];

  return (
    <Card padded={false} className="overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-700/60">
        <h2 className="text-sm font-bold text-white uppercase tracking-wider">Structure checks</h2>
        <p className="text-xs text-slate-500 mt-0.5">Re-run on every scan</p>
      </div>
      <CheckRow
        ok={badPointers.length === 0}
        label="Root pointers exist and parse"
        detail={
          badPointers.length === 0
            ? 'app-signed + platform-signed'
            : badPointers.map((p) => `${p.key}: ${p.error}`).join(' · ')
        }
        count={`${badPointers.length} failing`}
      />
      <CheckRow
        ok={status.strayKeys.length === 0}
        warn
        label="Keys match apks/<app>/<versionCode>/<variant>/"
        detail={
          status.strayKeys.length === 0
            ? 'no stray or misplaced keys found'
            : status.strayKeys.slice(0, 3).join(' · ')
        }
        count={`${status.strayKeys.length} stray`}
      />
      <CheckRow
        ok={brokenFolders.length === 0}
        label="Every build folder has both files with a valid manifest"
        detail={
          firstBroken
            ? `${firstBroken.appDir}/${firstBroken.versionDir}/${firstBroken.channel}: ${firstBroken.manifestError ?? 'app.apk is missing'}`
            : 'app.apk + manifest.json present and parseable'
        }
        count={`${brokenFolders.length} failing`}
      />
      <CheckRow
        ok={status.missingVariants.length === 0}
        warn
        label="Both signing variants present per version"
        detail={
          firstMissing
            ? `${firstMissing.appDir}/${firstMissing.versionDir} has no ${firstMissing.missing} build`
            : 'app-signed + platform-signed for every version code'
        }
        count={`${status.missingVariants.length} missing`}
      />
      <CheckRow
        ok={noSha.length === 0}
        warn
        label="Manifests include sha256"
        detail="older builds fall back to versionCode matching on promote"
        count={`${noSha.length} without`}
      />
      <CheckRow
        ok={status.legacyPointerKeys.length === 0}
        warn
        label="No legacy pointer keys (stable/, platform/)"
        detail={
          status.legacyPointerKeys.length === 0
            ? 'old-layout pointers fully retired'
            : status.legacyPointerKeys.join(' · ')
        }
        count={`${status.legacyPointerKeys.length} found`}
      />
    </Card>
  );
}

// ---- Bucket tree ----

interface VersionGroup {
  versionDir: string;
  folders: OtaFolderStatus[];
  missing: OtaMissingVariant[];
}

interface AppGroup {
  appDir: string;
  versions: VersionGroup[];
  hasIssue: boolean;
}

function folderHasIssue(f: OtaFolderStatus): boolean {
  return f.health !== 'healthy';
}

function groupFolders(status: OtaStructureStatus): AppGroup[] {
  const apps = new Map<string, Map<string, VersionGroup>>();
  for (const f of status.folders) {
    const versions = apps.get(f.appDir) ?? new Map<string, VersionGroup>();
    const group = versions.get(f.versionDir) ?? {
      versionDir: f.versionDir,
      folders: [],
      missing: [],
    };
    group.folders.push(f);
    versions.set(f.versionDir, group);
    apps.set(f.appDir, versions);
  }
  for (const mv of status.missingVariants) {
    apps.get(mv.appDir)?.get(mv.versionDir)?.missing.push(mv);
  }
  return [...apps.entries()].map(([appDir, versions]) => {
    const versionGroups = [...versions.values()];
    return {
      appDir,
      versions: versionGroups,
      hasIssue: versionGroups.some(
        (v) => v.missing.length > 0 || v.folders.some(folderHasIssue),
      ),
    };
  });
}

function FolderBadge({ folder }: { folder: OtaFolderStatus }) {
  if (folder.health === 'broken') return <Badge tone="error">Broken</Badge>;
  if (folder.isPromoted) return <Badge tone="blue">Live</Badge>;
  if (folder.health === 'degraded') return <Badge tone="warning">No sha256</Badge>;
  return <Badge tone="neutral">Inactive</Badge>;
}

function FileChip({
  name,
  exists,
  degraded = false,
  detail,
}: {
  name: string;
  exists: boolean;
  degraded?: boolean;
  detail: string;
}) {
  return (
    <span
      className={cx(
        'inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md border font-mono text-[11px]',
        !exists
          ? 'border-red-500/40 bg-red-500/5 text-red-400'
          : degraded
            ? 'border-amber-500/30 bg-slate-900/40 text-amber-400'
            : 'border-slate-700 bg-slate-900/40 text-slate-400',
      )}
    >
      {name === 'app.apk' ? (
        <CubeIcon className="w-3 h-3" />
      ) : (
        <DocumentTextIcon className="w-3 h-3" />
      )}
      {name}
      <span className="text-slate-500">{detail}</span>
    </span>
  );
}

function VariantRow({ folder }: { folder: OtaFolderStatus }) {
  return (
    <div
      className={cx(
        'flex items-center gap-2 flex-wrap py-1.5 pl-12 pr-4 border-l border-slate-700/50 ml-8',
        folderHasIssue(folder) && 'bg-red-500/5',
      )}
    >
      <span className="text-[12px] font-mono text-slate-400">{folder.channel}/</span>
      <FileChip
        name="app.apk"
        exists={folder.apk.exists}
        detail={folder.apk.exists ? fmtBytes(folder.apk.sizeBytes) : 'missing'}
      />
      <FileChip
        name="manifest.json"
        exists={folder.manifest.exists}
        degraded={folder.manifestValid && !folder.hasSha256}
        detail={
          !folder.manifest.exists
            ? 'missing'
            : !folder.manifestValid
              ? (folder.manifestError ?? 'invalid')
              : folder.hasSha256
                ? 'sha256 ✓'
                : 'no sha256'
        }
      />
      <span className="flex-1" />
      <span className="text-[10px] text-slate-500 font-mono">{ago(folder.uploadedAt)}</span>
      <FolderBadge folder={folder} />
    </div>
  );
}

function BucketTreeCard({
  status,
  issuesOnly,
  onToggleIssuesOnly,
}: {
  status: OtaStructureStatus;
  issuesOnly: boolean;
  onToggleIssuesOnly: () => void;
}) {
  const [collapsed, setCollapsed] = useState<Set<string>>(new Set());
  const groups = useMemo(() => groupFolders(status), [status]);

  const visible = groups
    .filter((g) => !issuesOnly || g.hasIssue)
    .map((g) => ({
      ...g,
      versions: g.versions.filter(
        (v) => !issuesOnly || v.missing.length > 0 || v.folders.some(folderHasIssue),
      ),
    }));

  const toggle = (appDir: string) =>
    setCollapsed((old) => {
      const next = new Set(old);
      if (next.has(appDir)) next.delete(appDir);
      else next.add(appDir);
      return next;
    });

  return (
    <Card padded={false} className="overflow-hidden">
      <div className="px-5 py-4 border-b border-slate-700/60 flex items-center gap-4">
        <div className="flex-1">
          <h2 className="text-sm font-bold text-white uppercase tracking-wider">Bucket tree</h2>
          <p className="text-xs text-slate-500 mt-0.5">
            apks/ · {status.summary.folders} build folders · grouped by app
          </p>
        </div>
        <label className="flex items-center gap-2 text-xs text-slate-400 cursor-pointer select-none">
          <input
            type="checkbox"
            checked={issuesOnly}
            onChange={onToggleIssuesOnly}
            className="accent-cyan-400"
          />
          Issues only
        </label>
        <Btn variant="outline" size="sm" onClick={() => setCollapsed(new Set())}>
          Expand all
        </Btn>
        <Btn
          variant="outline"
          size="sm"
          onClick={() => setCollapsed(new Set(groups.map((g) => g.appDir)))}
        >
          Collapse
        </Btn>
      </div>

      {visible.length === 0 && (
        <div className="px-5 py-8 text-center text-sm text-slate-500">
          {issuesOnly ? 'No issues — every build folder is healthy.' : 'The bucket has no build folders yet.'}
        </div>
      )}

      <div className="py-1">
        {visible.map((g) => {
          const isCollapsed = collapsed.has(g.appDir);
          const app = appOf(g.appDir as OtaAppId);
          return (
            <div key={g.appDir}>
              <button
                type="button"
                onClick={() => toggle(g.appDir)}
                className="w-full flex items-center gap-2 px-4 py-2 hover:bg-slate-700/20 transition-colors text-left"
              >
                <ChevronRightIcon
                  className={cx(
                    'w-3.5 h-3.5 text-slate-500 transition-transform',
                    !isCollapsed && 'rotate-90',
                  )}
                />
                <FolderIcon className="w-4 h-4 text-slate-500" />
                <span className="text-[13px] font-mono text-white">apks/{g.appDir}/</span>
                {app && <AppBadge app={app.id} size="sm" />}
                <span className="text-[11px] text-slate-500">
                  {app ? `${app.name} · ${app.packageId}` : ''}
                </span>
                <span className="flex-1" />
                {g.hasIssue ? (
                  <Badge tone="error">Needs attention</Badge>
                ) : (
                  <Badge tone="success">Healthy</Badge>
                )}
              </button>

              {!isCollapsed &&
                g.versions.map((v) => (
                  <div key={v.versionDir} className="pb-1">
                    <div className="flex items-center gap-2 pl-10 pr-4 py-1">
                      <ArchiveBoxIcon className="w-3.5 h-3.5 text-slate-600" />
                      <span className="text-[12px] font-mono text-slate-300">
                        {v.versionDir}/
                      </span>
                      {v.missing.map((mv) => (
                        <Badge key={mv.missing} tone="warning">
                          {mv.missing} missing
                        </Badge>
                      ))}
                    </div>
                    {v.folders.map((f) => (
                      <VariantRow key={f.prefix} folder={f} />
                    ))}
                  </div>
                ))}
            </div>
          );
        })}
      </div>
    </Card>
  );
}

export default OtaStatusPage;
